import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * All booking times in this app are displayed and entered in
 * Eastern Time (America/New_York). The studio operates on ET and
 * confirmations / invoices must always show ET to avoid confusing
 * customers in other timezones.
 */
export const ET_TZ = "America/New_York";

/** Format an ISO timestamp as a full ET date+time (e.g. "Mon, Jun 23, 2026, 2:30 PM EDT"). */
export function formatEastern(
  iso: string | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = {},
): string {
  if (!iso) return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: ET_TZ,
    timeZoneName: "short",
    ...opts,
  });
}

/** Time-only ET formatter (e.g. "2:30 PM EDT"). */
export function formatEasternTime(iso: string | Date | null | undefined): string {
  if (!iso) return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: ET_TZ,
    timeZoneName: "short",
  });
}

/** Date-only ET formatter. */
export function formatEasternDate(iso: string | Date | null | undefined): string {
  if (!iso) return "";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", timeZone: ET_TZ,
  });
}

/** Convert an ISO timestamp to a `datetime-local`-compatible string in ET wall clock. */
export function isoToEasternInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const o: Record<string, string> = {};
  for (const p of parts) o[p.type] = p.value;
  const hh = o.hour === "24" ? "00" : o.hour;
  return `${o.year}-${o.month}-${o.day}T${hh}:${o.minute}`;
}

/** Treat a `datetime-local` value as ET wall clock and convert to UTC ISO. */
export function easternInputToISO(local: string): string {
  if (!local) return "";
  const [date, time] = local.split("T");
  if (!date || !time) return "";
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  const asUTC = Date.UTC(y, mo - 1, d, h, mi);
  // Determine ET UTC offset at that moment.
  const tzParts = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TZ, timeZoneName: "shortOffset",
  }).formatToParts(new Date(asUTC));
  const off = tzParts.find(p => p.type === "timeZoneName")?.value ?? "GMT-5";
  const m = off.match(/GMT([+-]?\d+)(?::?(\d+))?/);
  const hours = m ? parseInt(m[1], 10) : -5;
  const mins = m && m[2] ? parseInt(m[2], 10) : 0;
  const offsetMs = (hours * 60 + (hours < 0 ? -mins : mins)) * 60_000;
  return new Date(asUTC - offsetMs).toISOString();
}
